import { useEffect } from 'react';
import { Sentry } from '../../lib/sentry';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';
import { seedCaptureSession } from '../../lib/capture-seed';

// Dev-only screenshot harness bypass. In a prod build `import.meta.env.DEV` is
// statically false, so this const folds to false and the seed branch (plus the
// capture-seed module) is dead-code-eliminated. Normal `npm run dev` never sets
// VITE_CAPTURE_MODE, so it's a no-op there too — only the capture launcher
// (scripts/capture-landing-shots.ts) turns it on.
const CAPTURE_MODE = import.meta.env.DEV && import.meta.env.VITE_CAPTURE_MODE === '1';

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const initialize = useAuthStore(s => s.initialize);
  const initialized = useAuthStore(s => s.initialized);

  useEffect(() => {
    if (CAPTURE_MODE) {
      // Skip the real Supabase init entirely — seed a signed-in demo family so
      // the gated app surfaces render for the marketing screenshot pass.
      seedCaptureSession();
      return;
    }
    initialize().then(() => {
      // Set Sentry user context so errors are attributed to the right account
      const { user, profile } = useAuthStore.getState();
      if (user) {
        Sentry.setUser({ id: user.id, email: profile?.email ?? undefined });
      }

      // Persona backfill for OAuth (Google) signups: the signUp() call with
      // options.data only works for email/password. OAuth users arrive with
      // empty user_metadata.persona; if localStorage has a persona from the
      // signup form, write it once now so attribution surveys aren't lost.
      if (user && !user.user_metadata?.persona) {
        try {
          const stashed = localStorage.getItem('hsy.signup.persona');
          if (stashed && ['parent', 'educator', 'both'].includes(stashed)) {
            supabase.auth.updateUser({ data: { persona: stashed } }).catch(() => { /* best-effort */ });
          }
        } catch { /* localStorage blocked / quota */ }
      }
    });
  }, [initialize]);

  // When the device comes back online, re-verify subscription with Supabase
  // so the user is immediately restored to their paid tier
  useEffect(() => {
    const handleOnline = () => {
      const store = useAuthStore.getState();
      if (store.user) {
        store.refreshSubscription();
      }
    };
    window.addEventListener('online', handleOnline);
    return () => window.removeEventListener('online', handleOnline);
  }, []);

  // Request persistent storage to prevent iOS Safari from evicting our data.
  // Chrome auto-grants for installed PWAs. Safari may prompt but significantly
  // reduces risk of localStorage/IndexedDB being wiped after 7+ days of inactivity.
  useEffect(() => {
    async function requestPersistence() {
      if (navigator.storage?.persist) {
        const granted = await navigator.storage.persist();
        if (!granted) {
          Sentry.captureMessage('Persistent storage not granted — data may be evicted', 'warning');
        }
      }
    }
    requestPersistence();
  }, []);

  // Only block rendering until the initial session check completes.
  // Do NOT gate on `loading` — auth actions (signUp, signIn) toggle loading
  // transiently and would unmount the entire app tree mid-flight, destroying
  // form state and swallowing errors/navigation.
  if (!initialized) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100">
        <div className="flex flex-col items-center gap-4">
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 rounded-full border-4 border-indigo-200" />
            <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-indigo-500 animate-spin" />
          </div>
          <p className="text-indigo-600 font-medium text-sm">Loading HomeSchool.You...</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
